import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, type User } from '@chicare/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { useSystemConfig } from '@/lib/queries';
import { sessionKeys, useLogout, useSession } from '@/lib/session';
import { Icon, type IconName } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { IdentityCard } from './IdentityCard';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';

/**
 * The account tab — and, until now, a dead end.
 *
 * The bottom nav has always pointed here and there was no route behind it, so
 * tapping Account dropped a buyer on the 404 page. That made ChiCare an app you
 * could browse and never sign into, which is a strange kind of broken: nothing
 * errors, you simply cannot get in.
 *
 * ── WHY THERE IS NO SEPARATE "REGISTER" BUTTON ──────────────────────────────
 *
 * For a buyer, signing in and registering are the same action. A phone number
 * or an email address that has never been seen creates an account the first
 * time its code is verified — no form, no password, no confirmation link.
 * Offering two buttons that do exactly the same thing would only make people
 * wonder which one they are supposed to press.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ANY MORE ──────────────────────────────────
 *
 * The pharmacy console and the rider app used to be listed at the bottom under
 * "Partner sign-in". They are gone. Both have their own door at their own URL,
 * and a pharmacist or a rider reaches it the way they always actually did — by
 * being told it when they were taken on, not by finding it on a shopping
 * screen. Advertising a staff-ish door to the public buys nothing and invites
 * somebody to rattle the handle.
 *
 * The full seller pitch card is gone from here too. It is three paragraphs of
 * recruitment on a screen somebody opened to sign in, and a screen that argues
 * with you before it lets you in is a screen people learn to scroll past. What
 * is left is one row each for the two things a member of the public can
 * actually apply to be, with the argument moved behind "Read full details"
 * where somebody who wants it will go looking.
 *
 * ChiCare staff were never listed here and still are not. Everyone on this
 * screen is a member of the public we are recruiting; staff are not.
 */
export function AccountScreen() {
  const { user, isLoading, isUnauthenticated } = useSession();
  const logout = useLogout();

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[560px] space-y-4 px-4 py-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full" radius="card" />
      </div>
    );
  }

  return (
    <>
      {/* Back, because Account is a hub: everything reached from here — the
          pharmacy pitch, an order, a policy page — is somewhere a buyer has to
          be able to come back from. */}
      <AppBar back title="Account" />

      <div className="mx-auto w-full max-w-[560px] px-4 pb-6 pt-7 sm:pt-6">
        {/* The app bar carries this title below 1280px, where it is hidden.
            Two "Account" headings stacked on a phone is just noise.

            Signed in, it greets by name instead. "Account" is a label on a
            drawer; a name is the app knowing who walked in, and on a screen
            whose whole job is "this is you" that is the more useful of the
            two. The first name only — "Welcome back, Okonji Emmanuel" reads
            like a letter from a bank. */}
        <h1 className="m-0 mb-4 hidden font-display text-[24px] font-bold tracking-[-0.02em] xl:block">
          {user && !isUnauthenticated ? `Welcome back, ${firstName(user.name)}` : 'Account'}
        </h1>

        {isUnauthenticated || !user ? (
          <SignedOut />
        ) : (
          <SignedIn user={user} onSignOut={() => logout.mutate()} />
        )}

        <PartnerRows />
      </div>
    </>
  );
}

/**
 * Two actions, and one line saying why there is no password field.
 *
 * ── WHY THAT LINE STAYS WHEN EVERYTHING ELSE WENT ─────────────────────────
 *
 * Because its absence is the thing people find strange. Every other shop asks
 * for a password, so a sign-in screen with none reads as unfinished unless it
 * says, once and briefly, that a code is coming instead.
 *
 * ONE line, under the buttons rather than above them. It answers a question
 * somebody has after they look, not before — and a paragraph here is a
 * paragraph standing between somebody and the button they came to press.
 */
/**
 * The name somebody is actually called.
 *
 * Nigerian buyers enter a full name, often three parts, and a greeting that
 * reads the whole thing back sounds like a letter from a bank rather than a
 * shop that knows them.
 *
 * Falls back to the whole string when there is no space in it, and to nothing
 * at all for the placeholder the API writes when a legacy account has no real
 * name — "Welcome back, ChiCare buyer" is worse than no greeting.
 */
function firstName(full: string | null | undefined): string {
  const trimmed = (full ?? '').trim();

  if (!trimmed || trimmed === 'ChiCare buyer') return 'there';

  return trimmed.split(/\s+/)[0] ?? trimmed;
}

function SignedOut() {
  return (
    <Card className="mb-5">
      <div className="grid gap-2.5 sm:grid-cols-2">
        <Link
          to="/sign-in?new=1"
          data-testid="account-create"
          className="flex min-h-12 items-center justify-center rounded-btn bg-blue px-5 text-[14.5px] font-bold text-white no-underline"
        >
          Create account
        </Link>

        <Link
          to="/sign-in"
          data-testid="account-signin"
          className="flex min-h-12 items-center justify-center rounded-btn border border-line bg-white px-5 text-[14.5px] font-bold text-ink no-underline"
        >
          Sign in
        </Link>
      </div>

      {/* One line. The "you can browse without signing in" half went: anybody
          reading this is ALREADY browsing without signing in, so it was telling
          them something they had just finished proving to themselves. */}
      <p className="m-0 mt-3.5 text-[12.5px] leading-relaxed text-muted">
        No password — we send a code to your email and phone number.
      </p>
    </Card>
  );
}

function SignedIn({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  return (
    <>
      {/* Name, both contacts and the buyer's own face, on brand blue. The two
          contacts stay in full on purpose — see IdentityCard. */}
      <IdentityCard user={user} onSignOut={onSignOut} />

      <AddMissingContact user={user} />

      <div className="mb-4 overflow-hidden rounded-card border border-line bg-white">
        <AccountLink
          to="/orders"
          icon="orders"
          label="Your orders"
          hint="Track deliveries and see past orders"
        />
        <AccountLink
          to="/cart"
          icon="cart"
          label="Your cart"
          hint="Anything you have not checked out yet"
        />
        {/* Somewhere to fix an address that is not the middle of a checkout.
            Delivery is priced by where it lands, so a wrong LGA here is a
            wrong fee on every order. */}
        <AccountLink
          to="/account/addresses"
          icon="pin"
          label="Your addresses"
          hint="Where we deliver, and what that costs"
        />
        <AccountLink
          to="/prescriptions"
          icon="doc"
          label="Your prescriptions"
          hint="Send one to be priced, or see the replies"
        />
        {/* Live support, from the one screen somebody already comes to when
            something has gone wrong. The chat lives on its own page rather
            than as a bubble pinned over every screen — see SupportScreen. */}
        <AccountLink
          to="/support"
          icon="message"
          label="Support"
          hint="Chat with a real person about an order"
        />
        {/* Last in the list, and deliberately in it: this is where somebody
            lands when they are looking for help rather than for a product, and
            "how to spot a counterfeit pack" is the answer to a question that
            otherwise reaches support. */}
        <AccountLink
          to="/blog"
          icon="quote"
          label="Blog"
          hint="Medicines, safety, and how ChiCare works"
        />
      </div>
    </>
  );
}

function AccountLink({
  to,
  icon,
  label,
  hint,
}: {
  to: string;
  icon: IconName;
  label: string;
  hint: string;
}) {
  return (
    <Link
      to={to}
      className="flex min-h-[60px] items-center gap-3 border-b border-line px-4 py-3 no-underline last:border-b-0"
    >
      <Icon name={icon} size={19} className="shrink-0 text-muted" />
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold text-ink">{label}</span>
        <span className="block text-[12px] text-muted">{hint}</span>
      </span>
      <Icon name="chevron" size={16} className="shrink-0 text-faint" />
    </Link>
  );
}

/**
 * The second way in, for a buyer who only ever gave us one.
 *
 * ── WHY THIS IS WORTH A CARD OF ITS OWN ─────────────────────────────────────
 *
 * A buyer can skip past this at sign-in, and most will — they came to buy
 * medicine, not to fill in a profile. The cost of that skip only lands later,
 * on the day their SMS does not arrive and the account holding their order
 * history has exactly one door they cannot open.
 *
 * So it is asked again here, once, calmly, with the reason attached. It is
 * never a blocker: everything else on this screen works whether or not they
 * ever fill it in.
 *
 * A contact already on the account is not editable here. An email or a number
 * ChiCare holds is a channel one-time codes are sent to, so repointing one is a
 * way into the account and needs its own verification round — the API refuses
 * it, and offering an input that silently does nothing would be worse than
 * offering none.
 */
function AddMissingContact({
  user,
}: {
  user: { name: string; phone?: string | null; email?: string | null };
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');

  // ────────────────── THE NAME WAS NEVER ASKED FOR ──────────────────
  //
  // The API has always reported `name` among the missing fields, and
  // /auth/profile has always accepted one. This card asked for a phone or an
  // email and never for a name, so an account carrying the placeholder had no
  // way to stop carrying it — the greeting read "Welcome back, ChiCare buyer"
  // for ever, and nothing on any screen offered a way to change it.
  //
  // The placeholder is written by the API when an account has no real name.
  // Matching on the string is ugly and is the honest test: it is what the
  // server writes, and `missing` is not on the session payload the account
  // screen reads.
  const needsName = !user.name.trim() || user.name.trim() === 'ChiCare buyer';
  const needsPhone = !user.phone;
  const needsEmail = !user.email;

  // One at a time, name first. A buyer who opens their account to three empty
  // boxes closes it again; a buyer asked one short question answers it.
  const asking: 'name' | 'phone' | 'email' | null = needsName
    ? 'name'
    : needsPhone
      ? 'phone'
      : needsEmail
        ? 'email'
        : null;

  const save = useMutation({
    mutationFn: () =>
      api.updateProfile(
        asking === 'name'
          ? { name: value.trim() }
          : asking === 'phone'
            ? { phone: value.trim() }
            : { email: value.trim() },
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(sessionKeys.me(), result.user);
      setValue('');
    },
  });

  if (!asking) return null;

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  return (
    <Card className="mb-4">
      <h2 className="m-0 font-display text-[15px] font-bold tracking-[-0.01em]">
        {asking === 'name'
          ? 'What should we call you?'
          : asking === 'phone'
            ? 'Add a phone number'
            : 'Add an email address'}
      </h2>
      <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-muted">
        {asking === 'name'
          ? 'It goes on the delivery, so the rider knows who to ask for at your door.'
          : asking === 'phone'
            ? 'Your rider needs a number to reach you at your door, and it gives us a second place to send your sign-in codes.'
            : 'We will send your sign-in codes here as well as by text, so a message that never arrives cannot lock you out of your own orders.'}
      </p>

      <form
        className="mt-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Input
          label={
            asking === 'name' ? 'Your name' : asking === 'phone' ? 'Phone number' : 'Email address'
          }
          type={asking === 'phone' ? 'tel' : asking === 'email' ? 'email' : 'text'}
          inputMode={asking === 'phone' ? 'tel' : asking === 'email' ? 'email' : 'text'}
          autoComplete={asking === 'name' ? 'name' : asking === 'phone' ? 'tel' : 'email'}
          value={value}
          placeholder={
            asking === 'name'
              ? 'Okonji Emmanuel'
              : asking === 'phone'
                ? '0803 123 4567'
                : 'you@example.com'
          }
          error={fieldErrors[asking]?.[0]}
          onChange={(event) => setValue(event.target.value)}
        />

        <Button type="submit" className="mt-3" loading={save.isPending} disabled={!value.trim()}>
          Save
        </Button>
      </form>

      {save.error instanceof ApiError && !save.error.isValidation ? (
        <p role="alert" className="m-0 mt-3 text-[12px] font-medium text-danger">
          {save.error.message}
        </p>
      ) : null}
    </Card>
  );
}

/**
 * Where the other two public roles sign in.
 *
 * Deliberately understated — most people reading this screen are buyers — but
 * present, because the alternative is a rider on their first shift being told
 * a URL over the phone.
 *
 * ChiCare staff are not on this list. See the note at the top of this file.
 */
/**
 * The two things a member of the public can apply to be.
 *
 * ── WHY A ROW AND NOT A CARD ───────────────────────────────────────────────
 *
 * This screen is where somebody signs in. The recruitment pitch used to be a
 * tinted card with a heading, a paragraph and two buttons, sitting between the
 * sign-in box and everything else — an advert in the doorway. One row each
 * says the same thing: there is a way in, and here is where to read about it.
 *
 * The argument lives behind "Read full details", on the page an admin writes,
 * where somebody who is actually interested will go.
 *
 * ── WHY NONE OF THE WORDS ARE IN THIS FILE ─────────────────────────────────
 *
 * Both labels come from `seller_pitch` settings, like every other word of the
 * recruitment copy. The people on the phone to pharmacies and agrovet stores
 * find out what persuades them, and they can change it in Admin › Settings and
 * have it live on the next page load.
 *
 * Emptying a pitch heading removes its row, which is how one half of the drive
 * is switched off without touching the other — the same rule ApplyBlock follows
 * at the foot of the page behind "Read full details".
 */
function PartnerRows() {
  const { data: config } = useSystemConfig();
  const pitch = config?.seller_pitch;

  // Only offered when the slug points at a page that is actually published.
  // A "read full details" link to a 404 is worse than no link, and this is the
  // screen where a prospective seller forms their first impression.
  const details = pitch?.page_slug?.trim()
    ? config?.legal_pages?.find((page) => page.slug === pitch.page_slug.trim())
    : undefined;

  const rows = [
    {
      key: 'pharmacy',
      to: '/sell',
      // The CTA rather than the heading: `card_heading` is a question ("Run a
      // pharmacy?") written to open a pitch, and this is a label on a door.
      label: pitch?.card_cta?.trim() || 'Register your pharmacy',
      show: Boolean(pitch?.card_heading?.trim()),
    },
    {
      key: 'veterinary',
      to: '/sell?sector=veterinary',
      label: pitch?.vet_card_cta?.trim() || 'Register your vet practice',
      show: Boolean(pitch?.vet_card_heading?.trim()),
    },
  ].filter((row) => row.show);

  if (rows.length === 0) return null;

  return (
    <section data-testid="partner-rows">
      <h2 className="m-0 mb-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
        Sell on ChiCare
      </h2>

      <div className="overflow-hidden rounded-card border border-line bg-white">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex items-center gap-2 border-b border-line px-4 last:border-b-0"
          >
            {/* Two SEPARATE links, not one wrapping the other. A link inside a
                link is invalid markup and the browser resolves it by guessing,
                which on a touchscreen means the wrong one fires. */}
            <Link
              to={row.to}
              data-testid={`register-${row.key}`}
              className="flex min-h-[52px] min-w-0 flex-1 items-center text-[13.5px] font-semibold text-ink no-underline"
            >
              {row.label}
            </Link>

            {details ? (
              <Link
                to={`/pages/${details.slug}`}
                data-testid={`details-${row.key}`}
                className="flex min-h-11 shrink-0 items-center text-[12.5px] font-semibold text-blue no-underline hover:underline"
              >
                Read full details
              </Link>
            ) : null}

            <Icon name="chevron" size={15} className="shrink-0 text-faint" aria-hidden />
          </div>
        ))}
      </div>
    </section>
  );
}

